import React, { useCallback, useMemo, useRef } from "react";

import { Grid, Tooltip } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";

import { Agent } from "../../../models/agent";
import { CustomerPortalAgent } from "../../../models/customerPortalShipment";
import { ContainerStatus, Location } from "../../../models/marine.traffic";
import { IShipmentDetailItemProps, Shipment } from "../../../models/shipments";
import { convertNauticalMilesToKilometers } from "../../../util/converts";
import { ShipmentAgentType, ShipmentTrackingColumnType, ShipmentType } from "../../../util/enums";
import { SectionTitle } from "../styles";
import AgentInfoModal, { AgentInfoModalHandle } from "./components/AgentInfoModal";
import {
  Counter,
  FieldLabel,
  FieldText,
  InfoTag,
  WrapperInfoField,
  WrapperInfoForm,
  WrapperTitle,
  WrapperWithInfoTag,
} from "./styles";

interface Props {
  shipment?: Shipment;
  containerStatus?: ContainerStatus;
  isCustomerPortal?: boolean;
  isAirtracking?: boolean;
}

const ShipmentDetails = ({
  shipment,
  containerStatus,
  isCustomerPortal = false,
  isAirtracking = false,
}: Props) => {
  const { t: translate } = useTranslation();
  const locations = containerStatus?.shipmentLocation;
  const portsLocations = containerStatus?.transportationPlan?.locations || [];

  const finalPort = portsLocations?.find((value) => value.portId === containerStatus?.finalPortId);
  const originPort = portsLocations?.find(
    (value) => value.portId === containerStatus?.originPortId
  );

  const getPortName = (portName?: string, port?: Location) => {
    if (!portName) return "";

    if (isAirtracking && port) {
      const { locationName: city, locationCountry: country } = port;

      return `${portName}${city ? `, ${city}` : ""}${country ? `, ${country}` : ""}`;
    }

    return portName;
  };

  const agentInfoModalRef = useRef<AgentInfoModalHandle>(null);

  const lastActiveVesselName = useMemo(() => {
    const events =
      containerStatus &&
      containerStatus?.transportationPlan?.events.filter((event) => event?.eventStatus === "ACT");

    if (events) {
      const lastEvent = events[events.length - 1];

      const lastEventShipId = () => {
        if (lastEvent?.vessel?.shipId != null) return lastEvent?.vessel?.shipId;

        return undefined;
      };
      const vessels = containerStatus && containerStatus.transportationPlan.vessels;
      const lastActiveEvent = vessels?.find((vessel) => vessel.shipId === lastEventShipId());

      return lastActiveEvent?.vesselName;
    }

    return containerStatus?.vesselName;
  }, [containerStatus]);

  const lastActiveVesselType = useMemo(() => {
    const events =
      containerStatus &&
      containerStatus?.transportationPlan?.events.filter((event) => event?.eventStatus === "ACT");

    if (events) {
      const lastEvent = events[events.length - 1];

      const lastEventShipId = () => {
        if (lastEvent?.vessel?.shipId != null) return lastEvent?.vessel?.shipId;

        return undefined;
      };
      const vessels = containerStatus && containerStatus.transportationPlan.vessels;
      const lastActiveEvent = vessels?.find((vessel) => vessel.shipId === lastEventShipId());

      return lastActiveEvent?.vesselType;
    }

    return containerStatus?.vesselType;
  }, [containerStatus]);

  const distanceToGoNextPort = useMemo(() => {
    if (containerStatus?.nextPortDistanceToGo) {
      return `${convertNauticalMilesToKilometers(containerStatus?.nextPortDistanceToGo)} km`;
    }

    return null;
  }, [containerStatus]);

  const fieldsForShipmentTypeSea = useMemo(() => {
    if (isCustomerPortal) {
      return [
        {
          label: translate("vesselName"),
          value: lastActiveVesselName,
          type: ShipmentTrackingColumnType.VESSEL_NAME,
        },
        {
          label: translate("billOfLading"),
          value: shipment?.billOfLanding?.toUpperCase(),
          type: ShipmentTrackingColumnType.BILL_OF_LADING,
        },
        {
          label: shipment?.containers?.length
            ? `${translate("container")} #`
            : translate("containerNumber"),
          value: shipment?.containers?.length
            ? shipment?.containers
                ?.map((container) => container?.container?.toUpperCase())
                .join(", ")
            : shipment?.containerNumbers?.toUpperCase(),
          type: ShipmentTrackingColumnType.CONTAINER,
        },
      ];
    }

    return [
      {
        label: translate("billOfLanding"),
        value: shipment?.billOfLanding?.toUpperCase(),
        type: ShipmentTrackingColumnType.BILL_OF_LADING,
      },
      {
        label: translate("originPort"),
        value: containerStatus?.originPortName || locations?.departure?.locationName,
        type: ShipmentTrackingColumnType.ORIGIN_PORT,
      },
      {
        label: shipment?.containers?.length
          ? translate("containers")
          : translate("containerNumber"),
        value: shipment?.containers?.length
          ? shipment?.containers?.map((container) => container?.container?.toUpperCase()).join(", ")
          : shipment?.containerNumbers?.toUpperCase(),
        type: ShipmentTrackingColumnType.CONTAINER,
      },
      {
        label: translate("finalDestinationPort"),
        value: containerStatus?.finalPortName,
        type: ShipmentTrackingColumnType.FINAL_DESTINATION_PORT,
      },
      {
        label: translate("carrier"),
        value: containerStatus?.scac,
        type: ShipmentTrackingColumnType.CARRIER,
      },
      {
        label: translate("distanceToNextPort"),
        value: distanceToGoNextPort,
        type: ShipmentTrackingColumnType.FINAL_DESTINATION_PORT,
      },

      {
        label: translate("vesselName"),
        value: lastActiveVesselName,
        type: ShipmentTrackingColumnType.VESSEL_NAME,
      },
      (!shipment?.marineTrafficId || (shipment && isCustomerPortal)) && {
        label: translate("originAgent"),
        value: shipment?.shipmentAgents?.find((agent) => agent.type === ShipmentAgentType.ORIGIN)
          ?.agent?.entity,
        type: ShipmentTrackingColumnType.AGENT,
        data: shipment?.shipmentAgents?.find((agent) => agent.type === ShipmentAgentType.ORIGIN)
          ?.agent,
      },
      {
        label: translate("vesselType"),
        value: lastActiveVesselType,
        type: ShipmentTrackingColumnType.VESSEL_TYPE,
      },
      (!shipment?.marineTrafficId || (shipment && isCustomerPortal)) && {
        label: translate("destinationAgent"),
        value: shipment?.shipmentAgents?.find(
          (agent) => agent.type === ShipmentAgentType.DESTINATION
        )?.agent?.entity,
        type: ShipmentTrackingColumnType.AGENT,
        data: shipment?.shipmentAgents?.find(
          (agent) => agent.type === ShipmentAgentType.DESTINATION
        )?.agent,
      },
    ]?.filter((i) => i);
  }, [shipment, containerStatus, isCustomerPortal]);

  const fieldsForShipmentTypeAir = useMemo(() => {
    return [
      {
        label: translate("houseAirWaybill"),
        value: shipment?.airwayBill,
        type: ShipmentTrackingColumnType.BILL_OF_LADING,
      },
      {
        label: translate("flightNumber"),
        value: shipment?.flightNumber || containerStatus?.vesselName || "",
        type: ShipmentTrackingColumnType.FLIGHT_NUMBER,
      },
      {
        label: translate("originAirport"),
        value: getPortName(
          containerStatus?.originPortName || locations?.departure?.locationName,
          originPort
        ),
        type: ShipmentTrackingColumnType.ORIGIN_PORT,
        notTag: true,
      },
      {
        label: `${translate("shipment")} #`,
        value: shipment?.containerId,
        type: ShipmentTrackingColumnType.SHIPMENT,
      },
      {
        label: translate("airline"),
        value: shipment?.airline || containerStatus?.carrier || "",
        type: ShipmentTrackingColumnType.AIRLINE,
      },
      {
        label: translate("finalDestinationAirport"),
        value: getPortName(containerStatus?.finalPortName, finalPort),
        type: ShipmentTrackingColumnType.FINAL_DESTINATION_PORT,
        notTag: true,
      },
    ]?.filter((i) => i);
  }, [shipment]);

  const fields = useMemo((): IShipmentDetailItemProps<CustomerPortalAgent>[] => {
    switch (shipment?.shipmentType) {
      case ShipmentType.AIR:
        return fieldsForShipmentTypeAir;
      default:
        return fieldsForShipmentTypeSea as IShipmentDetailItemProps<CustomerPortalAgent>[];
    }
  }, [shipment, isCustomerPortal, locations]);

  const renderTagCounter = (value?: string) => {
    return (
      <Tooltip title={value || ""} placement="top">
        <Counter>{String(value?.split(", ").length || 0)}</Counter>
      </Tooltip>
    );
  };

  const renderTagInfo = (agent: CustomerPortalAgent) => {
    return (
      <React.Fragment>
        <InfoTag onClick={() => agentInfoModalRef?.current?.open(agent as Agent)}>
          <FiInfo size={20} color={colors.gray600} />
        </InfoTag>
        <AgentInfoModal ref={agentInfoModalRef} />
      </React.Fragment>
    );
  };

  const renderFields = (field: IShipmentDetailItemProps<CustomerPortalAgent>) => {
    const { label, value, data, notTag } = field;

    return (
      <WrapperWithInfoTag>
        <WrapperInfoField>
          <FieldLabel>
            {label}
            {value?.includes(",") && !notTag ? renderTagCounter(value) : null}
          </FieldLabel>
          <FieldText>{value || "--"}</FieldText>
        </WrapperInfoField>
        {data && renderTagInfo(data)}
      </WrapperWithInfoTag>
    );
  };

  return (
    <React.Fragment>
      <WrapperTitle>
        <SectionTitle>{translate("shipmentDetails")}</SectionTitle>
      </WrapperTitle>
      <WrapperInfoForm container direction="row" xs={12} spacing={3}>
        {fields?.map((field, index) => (
          <Grid item xs={12} md={6} lg={6} key={`${field.label}-${index}`}>
            {renderFields(field)}
          </Grid>
        ))}
      </WrapperInfoForm>
    </React.Fragment>
  );
};

export default ShipmentDetails;
